// Modal dialogs: the welcome/about screen and the text viewer used for the user
// manual and version history (both bundled as plain text, see src/ui/docs.ts).

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { VERSION } from '../../version';
import { DOCS, type DocId } from '../docs';
import { readBool, WELCOME_KEY, writeBool } from '../prefs';
import { Brand } from './Brand';

interface ModalProps {
  labelledBy: string;
  wide?: boolean;
  onClose: () => void;
  children: ReactNode;
}

export function Modal({ labelledBy, wide, onClose, children }: ModalProps) {
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const target =
      box.current?.querySelector<HTMLElement>('[data-autofocus]') ??
      box.current?.querySelector<HTMLElement>('button');
    target?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key === 'Tab' && box.current) {
        // Keep focus inside the dialog.
        const items = box.current.querySelectorAll<HTMLElement>(
          'button, a[href], input, select, [tabindex="0"]',
        );
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      prev?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`modal ${wide ? 'modal-wide' : ''}`}
      >
        {children}
      </div>
    </div>
  );
}

export function WelcomeDialog({
  onClose,
  onOpenDoc,
}: {
  onClose: () => void;
  onOpenDoc: (doc: DocId) => void;
}) {
  const id = useId();
  return (
    <Modal labelledBy={id} onClose={onClose}>
      <div className="welcome-brand">
        <Brand titleId={id} />
        <div className="meta-line mt-1">v{VERSION}</div>
      </div>
      <p>
        Turn Commodore 64 SID tunes into editable MIDI for Ableton Live and other DAWs: one track per voice, a
        drum track, arpeggios as chords, vibrato as pitch bend and controller lanes for filter and pulse
        width.
      </p>
      <p>
        Drop a .sid file (for example from the High Voltage SID Collection) or try the built-in demo tune.
        Everything runs in your browser — your files never leave your computer.
      </p>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn" onClick={() => onOpenDoc('manual')}>
          User Manual
        </button>
        <button type="button" className="btn" onClick={() => onOpenDoc('history')}>
          Version history
        </button>
      </div>
      <label className="flex cursor-pointer items-center gap-2 text-xs text-muted">
        <input
          type="checkbox"
          defaultChecked={readBool(WELCOME_KEY, true)}
          onChange={(e) => writeBool(WELCOME_KEY, e.target.checked)}
        />
        Show this welcome on startup
      </label>
      <div className="modal-actions">
        <button type="button" className="btn btn-primary btn-lg" data-autofocus onClick={onClose}>
          Get started
        </button>
      </div>
    </Modal>
  );
}

/** Opens text in a standalone window as a proper UTF-8 document (textContent keeps it injection-safe). */
function openTextWindow(title: string, text: string) {
  const win = window.open('', '_blank');
  if (!win) return;
  const doc = win.document;
  doc.open();
  doc.write(
    '<!doctype html><html><head><meta charset="utf-8"><title></title>' +
      '<style>body{margin:0;background:#14181c;color:#dfe7ec;' +
      'font:13px/1.5 "Cascadia Mono",Consolas,ui-monospace,monospace;}' +
      'pre{margin:0;padding:20px;white-space:pre-wrap;word-break:break-word;}</style>' +
      '</head><body><pre></pre></body></html>',
  );
  doc.close();
  doc.title = title;
  doc.querySelector('pre')!.textContent = text;
}

export function DocDialog({ doc, onClose }: { doc: DocId; onClose: () => void }) {
  const id = useId();
  const { title, text } = DOCS[doc];
  return (
    <Modal labelledBy={id} wide onClose={onClose}>
      <h2 id={id}>{title}</h2>
      <pre className="doc-pre" tabIndex={0}>
        {text}
      </pre>
      <div className="modal-actions">
        <button
          type="button"
          className="btn btn-lg"
          onClick={() => {
            openTextWindow(title, text);
            onClose();
          }}
        >
          Open in new window
        </button>
        <button type="button" className="btn btn-primary btn-lg" data-autofocus onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}
